import { MouseEvent, useEffect, useState } from 'react'

import { useTheme } from '@chakra-ui/react'
import { CheckIcon, RightChevronIcon } from '@verbit-ai/icons-library'
import { Popover } from '@verbit-ai/verbit-ui-library'

import { useSetThemeField } from 'src/contexts/ThemeContext'
import { useConfig } from 'src/contexts/ConfigurationContext'
import { useEditor } from 'src/contexts/EditorContext'
import { useFeatureFlag } from 'src/contexts/FeatureFlagContext'
import { analyticsService } from 'src/services/AnalyticsService'
import { THEME_TYPES } from 'src/themes/types'
import { LargeScreenDisplaySettings } from 'src/components/LargeScreenDisplaySettings/LargeScreenDisplaySettings'
import { Switch } from 'src/components/VerbitWrappers/Switch'
import { useTranscriptMetadata } from 'src/contexts/Transcript/MetadataContext'
import { HOTKEYS } from 'src/utils/hotkeys'

import { THEMES_PRESENTATION, ThemeToggleProps } from './types'
import * as S from './styles'
import { ThemeTextCustomization } from './ThemeTextCustomization'

// The switch's label re-dispatches each click to its hidden input, so a click bubbling out of the switch
// would reach the row's onClick twice on top of the switch's own onChange.
const stopSwitchClickPropagation = (event: MouseEvent) => event.stopPropagation()

export const ThemeToggle = (props: ThemeToggleProps) => {
    const { isOpen, onOpen, onClose, children } = props
    const {
        palette: {
            themeToggle: { iconColor },
        },
        themeType: activeThemeType,
    } = useTheme()
    const [config, setProp] = useConfig()
    const setThemeField = useSetThemeField()
    const [isTextCustomizationOpen, setIsTextCustomizationOpen] = useState(false)
    const [isLSDSettingsOpen, setIsLSDSettingsOpen] = useState(false)

    const transcriptMetadata = useTranscriptMetadata()
    const canHaveExtendedCustomization = transcriptMetadata?.viewMode === 'job'
    const isAllCapsDisplayAllowed = useFeatureFlag('allow_all_caps_display')
    const { isEditingLockAvailable, isEditingLocked, toggleEditingLock } = useEditor()

    useEffect(() => {
        if (isOpen) {
            analyticsService.track('Accessibility panel opened', {})
        }
    }, [isOpen])

    const openChromakeyMode = () => {
        onClose()
        setIsLSDSettingsOpen(true)
    }

    const onLockEditingClick = () => {
        toggleEditingLock('menu')
    }

    const toggleTimestamps = () => {
        setProp('isTimestampFormatsEnabled', (isEnabled) => !isEnabled)
    }

    const toggleShowSpeakerColorCode = () => {
        setProp('isShowSpeakerColorCodeEnabled', (isEnabled) => !isEnabled)
    }

    const toggleShowTranscriptInAllCaps = () => {
        setProp('shouldShowTranscriptInAllCaps', (isEnabled) => !isEnabled)
    }

    const themeToggleContainer = () => (
        <S.ThemeToggleContainer>
            {canHaveExtendedCustomization && (
                <>
                    {isEditingLockAvailable && (
                        <S.ThemeButton
                            onClick={onLockEditingClick}
                            aria-keyshortcuts={HOTKEYS.toggleEditingLock.ariaKeyshortcuts}
                            data-appcues-lock-editing-switch="lock-editing-button">
                            Lock editing
                            <S.ShortcutHint>{HOTKEYS.toggleEditingLock.presentation}</S.ShortcutHint>
                            <S.SwitchWrapper onClick={stopSwitchClickPropagation}>
                                <Switch
                                    isChecked={isEditingLocked}
                                    onChange={onLockEditingClick}
                                    ariaLabel="lock-editing"
                                    data-appcues-lock-editing-switch="lock-editing-switch"
                                />
                            </S.SwitchWrapper>
                        </S.ThemeButton>
                    )}

                    <S.ThemeButton onClick={toggleTimestamps} data-appcues-timestamp-formats-switch="timestamp-formats-button">
                        Show timestamp
                        <S.SwitchWrapper onClick={stopSwitchClickPropagation}>
                            <Switch
                                isChecked={config.isTimestampFormatsEnabled}
                                onChange={toggleTimestamps}
                                ariaLabel="timestamp-formats"
                                data-appcues-timestamp-formats-switch="timestamp-formats-switch"
                            />
                        </S.SwitchWrapper>
                    </S.ThemeButton>

                    <S.ThemeButton
                        onClick={toggleShowSpeakerColorCode}
                        data-appcues-show-speaker-color-code-switch="show-speaker-color-code-button">
                        Show speaker color code
                        <S.SwitchWrapper onClick={stopSwitchClickPropagation}>
                            <Switch
                                isChecked={config.isShowSpeakerColorCodeEnabled}
                                onChange={toggleShowSpeakerColorCode}
                                ariaLabel="show-speaker-color-code"
                                data-appcues-show-speaker-color-code-switch="show-speaker-color-code-switch"
                            />
                        </S.SwitchWrapper>
                    </S.ThemeButton>

                    {isAllCapsDisplayAllowed && (
                        <S.ThemeButton
                            onClick={toggleShowTranscriptInAllCaps}
                            data-appcues-show-transcript-in-all-caps-switch="show-transcript-in-all-caps-button">
                            Show transcript in ALL CAPS
                            <S.SwitchWrapper onClick={stopSwitchClickPropagation}>
                                <Switch
                                    isChecked={config.shouldShowTranscriptInAllCaps}
                                    onChange={toggleShowTranscriptInAllCaps}
                                    ariaLabel="show-transcript-in-all-caps"
                                    data-appcues-show-transcript-in-all-caps-switch="show-transcript-in-all-caps-switch"
                                />
                            </S.SwitchWrapper>
                        </S.ThemeButton>
                    )}
                </>
            )}

            <S.ThemeOptionList role="list">
                {THEME_TYPES.filter((themeType) => THEMES_PRESENTATION[themeType].isVisible).map((themeType) => {
                    const isSelected = themeType === activeThemeType

                    return (
                        <S.ThemeOptionItem key={themeType}>
                            <S.ThemeButton aria-pressed={isSelected} onClick={() => setThemeField('themeType', themeType)}>
                                {THEMES_PRESENTATION[themeType].name}
                                {isSelected && <CheckIcon color={iconColor} aria-hidden />}
                            </S.ThemeButton>
                        </S.ThemeOptionItem>
                    )
                })}
            </S.ThemeOptionList>

            <Popover
                pattern="disclosure"
                animateDisclosure
                contentAriaLabel="Custom text"
                usePortal
                triggerEl={
                    <S.ThemeButton onClick={() => setIsTextCustomizationOpen((isCustomizationOpen) => !isCustomizationOpen)}>
                        Custom text <RightChevronIcon color={iconColor} />
                    </S.ThemeButton>
                }
                trigger="click"
                isOpen={isTextCustomizationOpen}
                onClose={() => setIsTextCustomizationOpen(false)}
                placement="right-start"
                contentWidth="unset">
                <ThemeTextCustomization />
            </Popover>

            {canHaveExtendedCustomization && <S.ThemeButton onClick={openChromakeyMode}>Large screen display</S.ThemeButton>}
        </S.ThemeToggleContainer>
    )

    const onMainPopoverClose = () => {
        if (!isTextCustomizationOpen) {
            onClose()
        }
    }

    return (
        <>
            <Popover
                pattern="disclosure"
                animateDisclosure
                contentAriaLabel="Display settings"
                triggerEl={children}
                trigger="click"
                isOpen={isOpen}
                onOpen={onOpen}
                onClose={onMainPopoverClose}
                placement="top-end"
                boxShadow="verbit_floating_action_button_shadow"
                contentWidth="unset">
                {themeToggleContainer()}
            </Popover>
            <LargeScreenDisplaySettings isOpen={isLSDSettingsOpen} onClose={() => setIsLSDSettingsOpen(false)} />
        </>
    )
}
